/**
 * The front door for a first visit (D-193): what DIGU is, Create an account first, Sign in second, and the first three
 * lessons without an account. Returning players land on Sign in instead (lib/frontDoor.ts). Same layout as Sign in,
 * so desktop shows the art on the left and this on the right.
 */
import type { ReactElement } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button, Screen, TextLink } from "../components";
import { BrandAttributes, BrandHero } from "../components/BrandHero";
import { LegalLinks } from "../components/LegalLinks";
import { useSession } from "../lib/session";

export function WelcomeScreen(): ReactElement {
  const { status } = useSession();
  const navigate = useNavigate();
  // D-199: a signed-out link (a table, lobby, club or shop item) is carried through Create an account or Sign in.
  const raw = (useLocation().state as { from?: string } | null)?.from;
  const from = raw && raw.startsWith("/") && !raw.startsWith("//") && raw !== "/welcome" && raw !== "/signin" ? raw : undefined;
  if (status === "signed-in") return <Navigate to={from ?? "/"} replace />;
  return (
    <Screen title="Welcome" className="screen--signin screen--welcome">
      <BrandHero />
      <div className="content">
        <h1 className="d-xl signin__headline">Your cards.{" "}<br />Your move.</h1>
        <p className="body signin__sub">The Maldivian card game, played with your friends, your club and the whole country. Free to play, no email needed.</p>
        <BrandAttributes />
        <div className="door__actions">
          <Button onClick={() => navigate(from ? `/create-account?next=${encodeURIComponent(from)}` : "/create-account")} id="welcome-create">Create an account</Button>
          <Button kind="quiet" onClick={() => navigate("/signin", { state: from ? { from } : undefined })} id="welcome-signin">Sign in</Button>
        </div>
        <p className="signin__foot">New to DIGU?<TextLink to="/learn">Learn the game first</TextLink></p>
        <p className="cap text-secondary door__note">Three short lessons on a real table, no account needed.</p>
        <LegalLinks />
      </div>
    </Screen>
  );
}

/** "That page isn't here" (D-193): an unknown address used to land on Home without a word. */
export function NotFoundScreen(): ReactElement {
  const { status } = useSession();
  return (
    <Screen title="Not found" className="screen--single">
      <div className="content door__missing" data-testid="not-found">
        <h1 className="d-l">That page isn't here</h1>
        <p className="body text-secondary">The link may be old or mistyped. Nothing was changed.</p>
        <div className="door__actions">
          <Button to={status === "signed-in" ? "/" : "/welcome"}>{status === "signed-in" ? "Go to Home" : "Go to the start"}</Button>
          <Button kind="quiet" to="/learn">Learn DIGU</Button>
        </div>
      </div>
    </Screen>
  );
}
